import { useEffect, useState } from 'react'
import { DashboardPage } from '@astro/dashboard'
import PageLayout from './PageLayout'

/**
 * The shared dashboard inside this app's chrome.
 *
 * The dashboard pins two things to the bottom of our fixed navbar — the sticky
 * sidebar on desktop, the section bar on mobile — and needs its height to do
 * it. That height is NOT one number: the promo strip's quick-links row is
 * `hidden lg:flex` and the brand logo steps h-10 → h-12 at sm, so the header
 * measures roughly 104 / 113 / 130px across the three bands. Hard-coding any
 * one of them left a visible gap (or a clipped bar) in the other two.
 *
 * So we measure the real element instead. The header belongs to this app, so
 * this is where the measurement belongs — the dashboard package just consumes
 * a number and stays host-agnostic.
 */
function useHeaderHeight(selector) {
  const [height, setHeight] = useState(null)

  useEffect(() => {
    const el = document.querySelector(selector)
    if (!el) return undefined
    const read = () => setHeight(el.getBoundingClientRect().height)
    read()
    // ResizeObserver catches the breakpoint steps and the scrolled-state
    // restyle; the resize listener covers viewport changes that don't alter
    // the header's own box.
    const ro = new ResizeObserver(read)
    ro.observe(el)
    window.addEventListener('resize', read)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', read)
    }
  }, [selector])

  return height
}

export default function DashboardRoute() {
  const headerHeight = useHeaderHeight('#sticky-header')
  // Until the first measurement lands, fall back to the desktop height this
  // route used before — never 0, which would tuck the bar under the navbar.
  const stickyTop = headerHeight != null ? `${headerHeight}px` : '8.125rem'

  return (
    <PageLayout className="text-astroDark antialiased overflow-x-clip">
      {/* heroTopClass stays a static clearance (it only has to be >= the
          header, not exact); the pin points are the measured value. */}
      <DashboardPage
        heroTopClass="pt-30 lg:pt-32.5"
        stickyTop={stickyTop}
        mobileStickyTop={stickyTop}
      />
    </PageLayout>
  )
}
